<template>
  <v-dialog v-model="value" max-width="70vw">
    <v-card :loading="loading" :disabled="loading">
      <v-card-title class="justify-center">
          <span class="text-h5 sp">{{ $t('district_admins.create') }}</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.firstname"
                  :label="$t('district_admins.firstname')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.lastname"
                  :label="$t('district_admins.lastname')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.lastname2nd"
                  :label="$t('district_admins.lastname2nd')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.email"
                  :label="$t('district_admins.email')"
                  :rules="[rules.required, rules.email]"
                  required
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.dni"
                  :label="$t('district_admins.dni')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.birthDate"
                      :label="$t('district_admins.birth_date')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.birthDate"
                    @input="dateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="form.school_group_id"
                  :items="schoolGroups"
                  item-text="text"
                  item-value="value"
                  :label="$t('district_admins.school_group')"
                  :rules="[rules.required]"
                  :search-input.sync="schoolGroupSearch"
                  clearable
                  hide-no-data
                  no-filter
                  :placeholder="$t('district_admins.search_school_group')"
                  prepend-inner-icon="mdi-magnify"
                  required
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text @click="close">
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn color="deep-purple" text @click="save" :disabled="!valid">
          {{ $t('common.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import districtAdminService from '../../../services/districtAdmin.service';
import schoolGroupService from '../../../services/schoolGroup.service';
import snackbarMixin from '../../../mixins/snackbar';

export default {
  name: 'CreateDistrictAdminModal',
  components: {
  },
  props: {
    value: { type: Boolean, default: false },
  },
  data: () => ({
    loading: false,
    valid: false,
    dateMenu: false,
    schoolGroups: [],
    allSchoolGroups: [], // All school groups loaded
    schoolGroupSearch: '', // Search input
    form: {
      firstname: '',
      lastname: '',
      lastname2nd: '',
      email: '',
      dni: '',
      birthDate: '',
      school_group_id: null,
    },
    rules: {
      required: (value) => !!value || 'Required.',
      email: (value) => {
        const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return pattern.test(value) || 'Invalid e-mail.';
      },
    },
  }),
  mixins: [snackbarMixin],
  computed: {
    ...mapState(['countries']),
  },
  mounted() {
    this.loadSchoolGroups();
  },
  watch: {
    schoolGroupSearch(val) {
      if (val && val.length > 0) {
        this.filterSchoolGroups(val);
      } else {
        this.schoolGroups = [...this.allSchoolGroups];
      }
    },
  },
  methods: {
    async loadSchoolGroups() {
      try {
        const response = await schoolGroupService.getAll();
        this.allSchoolGroups = response.data || [];
        this.schoolGroups = [...this.allSchoolGroups];
        this.schoolGroupSearch = '';
      } catch (error) {
        this.schoolGroups = [];
        this.allSchoolGroups = [];
      }
    },
    filterSchoolGroups(search) {
      const searchLower = search.toLowerCase();
      this.schoolGroups = this.allSchoolGroups.filter((group) => {
        const groupText = group.text.toLowerCase();
        return groupText.includes(searchLower);
      });
    },
    async save() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          await districtAdminService.createDistrictAdmin(this.form);
          this.$emit('onSave');
          this.resetForm();
        } catch (error) {
          const errorMessage = this.getErrorMessage(error);
          this.snackbarError(errorMessage);
        } finally {
          this.loading = false;
        }
      }
    },
    close() {
      this.resetForm();
      this.$emit('onCancel');
    },
    resetForm() {
      this.form = {
        firstname: '',
        lastname: '',
        lastname2nd: '',
        email: '',
        dni: '',
        birthDate: '',
        school_group_id: null,
      };
      this.schoolGroupSearch = '';
      if (this.allSchoolGroups.length > 0) {
        this.schoolGroups = [...this.allSchoolGroups];
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    getErrorMessage(error) {
      if (error.response && error.response.data) {
        const { message, title } = error.response.data;
        return message || title || 'Error al crear administrador de distrito';
      }
      return error.message || 'Error al crear administrador de distrito';
    },
  },
};
</script>

<style scoped>
span.sp{
    font-family: "Space Grotesk" !important;
}
</style>
